class AcFavoritesOverlay extends HTMLElement {
  #events;
  #reads;
  #generation = 0;
  #open = false;
  #rows = new Map();
  #mounted = new WeakMap();
  #quantities = new Map();
  #bindings = new Map();
  #recommendations = new Map();
  #recommendationRead;
  #recommendationGeneration = 0;
  #adding = new Set();
  #desktop = matchMedia('(min-width: 1024px)');
  #motion = matchMedia('(prefers-reduced-motion: reduce)');
  #removals = new Set();
  #reduced = () => this.#motion.matches || document.body.dataset.acMotion === 'reduced';
  #finishRemovals = () => this.#removals.forEach((animation) => animation.finish());
  #syncMotion = () => { if (this.#reduced()) this.#finishRemovals(); };

  connectedCallback() {
    this.#events?.abort();
    this.#events = new AbortController();
    const signal = this.#events.signal;
    const motionObserver = new MutationObserver(this.#syncMotion);
    motionObserver.observe(document.body, { attributes: true, attributeFilter: ['data-ac-motion'] });
    this.#motion.addEventListener('change', this.#syncMotion, { signal });
    signal.addEventListener('abort', () => { motionObserver.disconnect(); this.#finishRemovals(); }, { once: true });
    document.addEventListener('click', this.#click, { signal });
    document.addEventListener('ac:wishlist:changed', this.#changed, { signal });
    document.addEventListener('ac:overlay-open', (event) => {
      if (event.detail?.name !== 'wishlist') return;
      this.#open = true;
      this.#reconcile();
      this.querySelector('[data-ac-wishlist-items]').scrollTop = 0;
      this.#refresh();
      this.#loadRecommendations();
    }, { signal });
    document.addEventListener('ac:overlay-close', (event) => {
      if (event.detail?.name !== 'wishlist') return;
      this.#open = false;
      this.#finishRemovals();
      this.#reads?.abort();
      this.#generation++;
      this.#recommendationRead?.abort();
      this.#recommendationGeneration++;
      this.#stopRail();
    }, { signal });
    this.#desktop.addEventListener('change', () => this.#startRail(), { signal });
    this.#motion.addEventListener('change', () => this.#startRail(), { signal });
    if (this.#hasPage()) {
      this.#reconcile();
      this.#refresh();
    }
  }

  disconnectedCallback() {
    this.#events?.abort();
    this.#reads?.abort();
    this.#recommendationRead?.abort();
    this.#generation++;
    this.#recommendationGeneration++;
    this.#stopRail();
    this.#bindings.forEach(dispose => dispose());
    this.#bindings.clear();
  }

  #items() { return window.acWishlistStore?.items || []; }
  #hasPage() { return Boolean(document.querySelector('[data-ac-wishlist-view="page"]')); }
  #targets() { return [...document.querySelectorAll('[data-ac-wishlist-items]')]; }
  #changed = () => {
    if (this.#open || this.#hasPage()) {
      this.#reconcile();
      this.#refresh(false);
    }
    if (this.#open) this.#loadRecommendations();
  };

  #reconcile() {
    const items = this.#items();
    const ids = new Set(items.map(item => String(item.id)));
    for (const [id] of this.#rows) if (!ids.has(id)) this.#rows.delete(id);
    for (const [id] of this.#quantities) if (!ids.has(id)) this.#quantities.delete(id);
    this.#targets().forEach(target => {
      if (!target.hasAttribute('tabindex')) target.tabIndex = -1;
      [...target.querySelectorAll('[data-ac-wishlist-item]')].forEach(row => {
        if (!ids.has(row.dataset.acWishlistItem)) {
          const focus = row.contains(document.activeElement);
          const next = row.nextElementSibling?.querySelector('button,a') || row.previousElementSibling?.querySelector('button,a');
          this.#dispose(row);
          row.remove();
          if (focus) (next || target).focus({preventScroll:true});
        }
      });
      if (!items.length) {
        if (!target.querySelector('.ac-favorites__empty')) target.replaceChildren(this.querySelector('[data-ac-favorites-empty]').content.cloneNode(true));
        return;
      }
      target.querySelector('.ac-favorites__empty')?.remove();
      const rowsById = new Map([...target.children].map(row => [row.dataset.acWishlistItem, row]));
      for (const item of items) {
        let row = rowsById.get(String(item.id));
        if (!row) {
          row = this.querySelector('[data-ac-favorites-pending]').content.firstElementChild.cloneNode(true);
          row.dataset.acWishlistItem = item.id;
          const link = row.querySelector('a');
          const url = this.#productURL(item);
          link.href = url?.href || this.dataset.rootUrl || '/';
          link.textContent = item.title;
          row.querySelector('[data-ac-wishlist-remove]').dataset.acWishlistRemove = item.id;
          row.querySelector('[data-ac-wishlist-retry]').dataset.acWishlistRetry = item.id;
          target.append(row);
        }
        const cached = this.#rows.get(String(item.id));
        if (cached && cached.variant === String(item.variantId) && this.#mounted.get(row) !== cached) row = this.#mount(target, row, cached);
        rowsById.set(String(item.id), row);
      }
      items.forEach((item, index) => {
        const row = rowsById.get(String(item.id));
        if (row && target.children[index] !== row) target.insertBefore(row, target.children[index] || null);
      });
    });
  }

  #productURL(item) {
    try {
      const url = new URL(item.url, location.origin);
      if (url.origin !== location.origin || !url.pathname.includes('/products/')) return null;
      url.search = '';
      if (item.variantId) url.searchParams.set('variant', item.variantId);
      return url;
    } catch { return null; }
  }

  async #refresh(force = true) {
    this.#reads?.abort();
    this.#reads = new AbortController();
    const signal = this.#reads.signal;
    const generation = ++this.#generation;
    const pending = this.#items().filter(item => force || this.#rows.get(String(item.id))?.variant !== String(item.variantId));
    let index = 0;
    const worker = async () => {
      while (index < pending.length && !signal.aborted) {
        const item = pending[index++];
        const id = String(item.id);
        // Keep a confirmed selection usable during its background stock refresh.
        if (this.#rows.get(id)?.variant !== String(item.variantId)) {
          this.#targets().forEach(target => target.querySelector('[data-ac-wishlist-item="' + CSS.escape(id) + '"] [data-ac-wishlist-add]')?.setAttribute('disabled', ''));
        }
        try {
          const url = this.#productURL(item);
          if (!url) throw Error('Invalid product URL');
          url.searchParams.set('section_id', 'ac-favorites-product');
          const response = await fetch(url, {signal, headers:{Accept:'text/html'}});
          if (!response.ok) throw Error('Product refresh failed');
          const template = document.createElement('template');
          template.innerHTML = await response.text();
          const row = template.content.querySelector('[data-ac-wishlist-item]');
          if (!row || row.dataset.acWishlistItem !== id) throw Error('Invalid product response');
          // Never substitute another variant when a saved selection was deleted.
          if (row.dataset.variantId !== String(item.variantId)) {
            row.dataset.available = 'false';
            row.querySelector('[data-ac-wishlist-add]')?.setAttribute('disabled', '');
            row.querySelector('[data-ac-wishlist-status]').textContent = this.dataset.readErrorLabel;
          }
          if (generation !== this.#generation || signal.aborted) return;
          const cached = {variant:String(item.variantId), html:row.outerHTML};
          this.#rows.set(id, cached);
          for (const target of this.#targets()) {
            const previous = target.querySelector('[data-ac-wishlist-item="' + CSS.escape(id) + '"]');
            if (previous) this.#mount(target, previous, cached);
          }
        } catch (error) {
          if (signal.aborted || generation !== this.#generation) return;
          this.#targets().forEach(target => {
            const row = target.querySelector('[data-ac-wishlist-item="' + CSS.escape(id) + '"]');
            if (!row) return;
            row.querySelector('[data-ac-wishlist-status]').textContent = this.dataset.readErrorLabel;
            row.querySelector('[data-ac-wishlist-retry]').hidden = false;
          });
        }
      }
    };
    await Promise.all(Array.from({length:Math.min(3,pending.length)}, worker));
  }

  #dispose(row) {
    this.#bindings.get(row)?.();
    this.#bindings.delete(row);
    window.AcLottieManager?.destroyWithin?.(row);
  }

  #mount(target, previous, cached) {
    const template = document.createElement('template');
    template.innerHTML = cached.html;
    const row = template.content.firstElementChild;
    const id = row.dataset.acWishlistItem;
    const view = target.dataset.acWishlistView;
    const focus = previous.contains(document.activeElement);
    const active = document.activeElement;
    const focusAttribute = ['data-ac-wishlist-add','data-ac-wishlist-remove','data-ac-wishlist-retry','data-ac-product-quantity','data-ac-quantity-minus','data-ac-quantity-plus'].find(name => active?.hasAttribute(name));
    row.querySelectorAll('[id]').forEach(node => {
      const oldId = node.id;
      node.id = view + '-' + oldId;
      row.querySelectorAll('[aria-labelledby]').forEach(control => { if(control.getAttribute('aria-labelledby') === oldId) control.setAttribute('aria-labelledby', node.id); });
    });
    this.#mounted.set(row, cached);
    this.#dispose(previous);
    previous.replaceWith(row);
    const update = (value, input) => {
      const quantity = window.AcProductQuantity.normalize(value, {min:input.min,max:input.max || null,increment:input.step});
      this.#quantities.set(id, quantity);
      this.#targets().forEach(t => {
        const control = t.querySelector('[data-ac-wishlist-item="' + CSS.escape(id) + '"] [data-ac-product-quantity-control]');
        const field = control?.querySelector('input');
        if (!field) return;
        field.value = quantity;
        control.querySelector('[data-ac-quantity-minus]').disabled = quantity <= Number(field.min || 1);
        control.querySelector('[data-ac-quantity-plus]').disabled = Boolean(field.max) && quantity + Number(field.step || 1) > Number(field.max);
      });
    };
    const controls = row.querySelectorAll('[data-ac-product-quantity-control]');
    if (window.AcProductQuantity) {
      this.#bindings.set(row, window.AcProductQuantity.bind(controls, update));
      const input = row.querySelector('[data-ac-product-quantity]');
      if (input) update(this.#quantities.get(id) ?? input.value, input);
    }
    if (this.#adding.has(id)) row.querySelector('[data-ac-wishlist-add]')?.setAttribute('disabled','');
    if (focus) (row.querySelector('[' + (focusAttribute || 'data-ac-wishlist-remove') + ']') || row).focus({preventScroll:true});
    return row;
  }

  #click = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-ac-wishlist-remove],[data-ac-wishlist-add],[data-ac-wishlist-retry]') : null;
    if (!button || !button.closest('[data-ac-wishlist-items]')) return;
    event.preventDefault();
    if (button.hasAttribute('data-ac-wishlist-retry')) { await this.#refresh(); return; }
    const row = button.closest('[data-ac-wishlist-item]');
    const id = row.dataset.acWishlistItem;
    if (button.hasAttribute('data-ac-wishlist-remove')) {
      if (row.dataset.removing) return;
      row.dataset.removing = 'true';
      let animation;
      if (!this.#reduced()) {
        animation = row.animate([{height:row.offsetHeight + 'px',opacity:1},{height:'0px',opacity:0,paddingBlock:'0px'}],{duration:400,easing:'ease',fill:'forwards'});
        this.#removals.add(animation);
        try { await animation.finished; } catch { /* Finishing the removal still applies the requested action. */ }
        finally { this.#removals.delete(animation); }
      }
      if (!window.acWishlistStore?.remove(id)) {
        animation?.cancel();
        delete row.dataset.removing;
        const status = row.querySelector('[data-ac-wishlist-status]');
        if (status) status.textContent = this.dataset.storageErrorLabel || this.dataset.errorLabel;
      }
      return;
    }
    if (this.#adding.has(id) || row.dataset.available !== 'true' || button.disabled) return;
    this.#adding.add(id);
    button.disabled = true;
    const status = row.querySelector('[data-ac-wishlist-status]');
    status.textContent = '';
    try {
      const input = row.querySelector('[data-ac-product-quantity]');
      const quantity = input ? window.AcProductQuantity.normalize(input.value,{min:input.min,max:input.max||null,increment:input.step}) : Number(button.dataset.min || 1);
      const payload = await window.AcCartActions.addItems([{id:row.dataset.variantId,quantity}]);
      await window.AcCartActions.sync(payload);
      status.textContent = this.dataset.addedLabel;
      document.querySelector('ac-overlay-system')?.announce(this.dataset.addedLabel);
    } catch { status.textContent = this.dataset.errorLabel; }
    finally {
      this.#adding.delete(id);
      this.#targets().forEach(target => {
        const current = target.querySelector('[data-ac-wishlist-item="' + CSS.escape(id) + '"]');
        if (current?.dataset.available === 'true') current.querySelector('[data-ac-wishlist-add]')?.removeAttribute('disabled');
      });
    }
  };

  #stopRail() {
    const rail = this.querySelector('[data-ac-favorites-rail]');
    if (!rail) return;
    window.ACRails?.destroyWithin(rail);
    rail.removeAttribute('data-ac-rail');
  }

  #startRail() {
    this.#stopRail();
    const rail = this.querySelector('[data-ac-favorites-rail]');
    if (!this.#open || !rail || rail.closest('aside').hidden) return;
    rail.dataset.acRail = '';
    rail.dataset.acRailInteractiveClones = 'true';
    rail.dataset.acRailAxis = this.#desktop.matches ? 'y' : 'x';
    const track = rail.querySelector('[data-ac-rail-track]');
    const overflows = this.#desktop.matches ? track.scrollHeight > rail.clientHeight : track.scrollWidth > rail.clientWidth;
    const autoplay = this.dataset.autoplay !== 'false' && !this.#motion.matches && overflows;
    rail.toggleAttribute('data-ac-rail-loop', autoplay);
    rail.dataset.acRailAutoplay = autoplay ? (this.#desktop.matches ? '130' : '45') : '0';
    window.ACRails?.init(rail);
  }

  async #loadRecommendations() {
    const region = this.querySelector('[data-ac-favorites-recommendations]');
    if (!region) return;
    const seed = this.#items()[0]?.productId;
    const smart = this.dataset.recommendationMode === 'smart' && seed;
    const key = smart ? String(seed) : 'collection';
    const generation = ++this.#recommendationGeneration;
    this.#recommendationRead?.abort();
    this.#recommendationRead = new AbortController();
    const signal = this.#recommendationRead.signal;
    try {
      let html = this.#recommendations.get(key);
      if (!html) {
        const fallback = new URL(this.dataset.rootUrl || '/', location.origin);
        fallback.searchParams.set('section_id','ac-favorites-recommendations');
        let url = fallback;
        if (smart) {
          url = new URL(this.dataset.recommendationsUrl || '/recommendations/products', location.origin);
          url.search = new URLSearchParams({section_id:'ac-favorites-recommendations', product_id:seed, limit:'10', intent:'related'}).toString();
        }
        let response;
        try { response = await fetch(url,{signal}); }
        catch (error) {
          if (!smart || signal.aborted) throw error;
          response = await fetch(fallback,{signal});
        }
        if (!response.ok && smart) response = await fetch(fallback,{signal});
        if (!response.ok) throw Error('Recommendations unavailable');
        html = await response.text();
        if (!html.includes('data-ac-favorites-candidates')) throw Error('Invalid recommendations');
        this.#recommendations.set(key,html);
        if (this.#recommendations.size > 8) this.#recommendations.delete(this.#recommendations.keys().next().value);
      }
      if (!this.#open || generation !== this.#recommendationGeneration || signal.aborted) return;
      const template = document.createElement('template');
      template.innerHTML = html;
      const excluded = new Set(this.#items().map(item => String(item.productId)));
      const selected = [...template.content.querySelectorAll('[data-ac-favorites-recommendation]')].filter(node => {
        const id = node.dataset.productId;
        if (excluded.has(id)) return false;
        excluded.add(id); return true;
      }).slice(0,Number(this.dataset.recommendationCount || 8));
      this.#stopRail();
      this.querySelector('[data-ac-rail-track]').replaceChildren(...selected);
      region.hidden = selected.length === 0;
      this.#startRail();
    } catch {
      if (signal.aborted || generation !== this.#recommendationGeneration) return;
      region.hidden = true;
      this.#stopRail();
    }
  }
}
if (!customElements.get('ac-favorites-overlay')) customElements.define('ac-favorites-overlay', AcFavoritesOverlay);
